import type { DateString } from '~/types/CommonTypes';
import type { MySsApiResponseData, MySsApiResponseDataList } from '~/types/MySsApiResponse';
import type { MySsException } from '~/types/MySsException';

import usePaginator from '~/composables/usePaginator';
import { Transaction, TransactionListFilters } from '~/models/Transaction';
import { TransactionAdapter, TransactionListFilterAdapter } from '~/adapters/TransactionAdapter';
import { defineStore } from 'pinia';
import {
  getBalance,
  getTransactions,
  makeARansomRequest,
  getRewardsAccounts,
} from '~/services/apiMyAccount';
import { useAuthStore } from '~/stores/useAuthStore';
import { useProfileStore } from '~/stores/useProfileStore';
import { useTimer } from '~/composables/useTimer';
import FeatureFlagService from '~/services/FeatureFlagService';
import { ref, computed, toRaw } from 'vue';

type TBalance = {
  availableBalance: number;
  lastUpdateDateTime: DateString;
  currencySymbol: string;
};

export const useTransactionsStore = defineStore(
  'MY_SCAN_SOURCE_TRANSACTIONS_STORE',
  () => {
    // #region States:
    const AUTH_STORE = useAuthStore();
    const PROFILE_STORE = useProfileStore();

    const { profile } = storeToRefs(PROFILE_STORE);

    const paginator = usePaginator();
    const timer = useTimer();
    const {
      pagination,
      CallPaginate,
      CallResetPagination,
      CallSetPagination,
      CallPaginateAndFetch,
    } = paginator;

    const balance = ref<TBalance>({} as TBalance);
    const transactionList = ref<Transaction[]>([]);
    const transactionListFilters = ref(new TransactionListFilters());
    const loadingTransactionList = ref<boolean>(true);
    const loadingBalance = ref<boolean>(false);
    const loadingRewardsAccounts = ref<boolean>(false);
    const allowRedeem = ref<boolean>(false);
    // #endregion

    // #region Getters:
    const getTransactionListState = computed(() => transactionList.value);
    const getBalanceState = computed(() => balance.value);
    const hasTransaction = computed(() => transactionList.value.length > 0);
    const isActiveFeature = computed(() => {
      if (FeatureFlagService !== null) {
        const { currentEconomicGroupMember } = AUTH_STORE.session;
        const whiteList = FeatureFlagService?.findByFlagName('WhiteListCommissions');

        return (
          FeatureFlagService?.isOn('CommissionsFeaturesAvailable') ||
          (Array.isArray(whiteList?.defaultValue) &&
            whiteList.defaultValue.includes(currentEconomicGroupMember))
        );
      }

      return false;
    });
    // #endregion

    // #region Actions:
    function startIsLoadingRewardsAccounts() {
      loadingRewardsAccounts.value = true;
    }
    function stopIsLoadingRewardsAccounts() {
      loadingRewardsAccounts.value = false;
    }

    function stopIsLoadingBalance() {
      loadingBalance.value = false;
    }
    function startIsLoadingBalance() {
      loadingBalance.value = true;
    }

    function stopIsLoadingTransactionList() {
      loadingTransactionList.value = false;
    }
    function startIsLoadingTransactionList() {
      loadingTransactionList.value = true;
    }

    function setBalance(data?: TBalance) {
      if (!data) balance.value = {} as TBalance;
      else balance.value = { ...data };
    }
    function setTransactionList(data?: Transaction[]) {
      if (!data) transactionList.value = [];
      else transactionList.value = [...data];
    }
    function $reset() {
      setTransactionList();
      setBalance();
      transactionListFilters.value = new TransactionListFilters();
      loadingTransactionList.value = false;
      loadingBalance.value = false;
      loadingRewardsAccounts.value = false;
      CallResetPagination();
    }
    async function paginate(value?: any) {
      const params = new TransactionListFilters();
      params.currentPage = (value.page ?? value?.currentPage ?? pagination.value.currentPage) + 1;
      params.pageSize = value?.rows ?? pagination.value.pageSize;

      return await dispatchGetTransactions(true, params);
    }
    // #endregion
    // #region Dispatchers:
    async function dispatchMakeARansomRequest(data: any) {
      const [day, month, year] = data.RedeemingInvoiceIssueDate.split('/');
      const monthIndex = Number.parseInt(month) - 1;
      const formData = new FormData();
      data.File = toRaw(data.File);
      formData.append('companyDocument', AUTH_STORE.session.currentEconomicGroupMember);
      formData.append('file', toRaw(data.File));
      formData.append('value', data.Value);
      formData.append('description', data.Description);
      formData.append('operationType', data.OperationType);
      formData.append('redeemingInvoiceNumber', data.RedeemingInvoiceNumber);
      formData.append('redeemingInvoiceSeries', data.RedeemingInvoiceSeries);
      formData.append(
        'redeemingInvoiceIssueDate',
        String(new Date(year, monthIndex, day).getTime()),
      );

      const res = await makeARansomRequest(formData);
      return res;
    }

    async function dispatchGetBalance(force = false) {
      if (!isActiveFeature.value) return;
      if (profile.value.isCommercial) return;
      if (!timer.isExpired.value && !force) return;

      startIsLoadingBalance();

      await getBalance(AUTH_STORE.session.currentEconomicGroupMember).then(
        ({ data }: MySsApiResponseData) => {
          setBalance(data);
          timer.startTimer();
          stopIsLoadingBalance();
        },
        (error: MySsException) => {
          setBalance();
          stopIsLoadingBalance();
          throw error;
        },
      );
    }

    async function dispatchGetTransactions(force = false, params = new TransactionListFilters()) {
      if (!isActiveFeature.value) return;
      if (profile.value.isCommercial) return;
      if (!timer.isExpired.value && !force) return;

      startIsLoadingTransactionList();

      const _params = TransactionListFilterAdapter.toDto({
        ...params,
        ...transactionListFilters.value,
        ...pagination.value,
      });

      _params.currentPage = params.currentPage;
      _params.pageSize = params.pageSize;
      await getTransactions(AUTH_STORE.session.currentEconomicGroupMember, _params).then(
        ({items, pagination, success}) => {
          if (!success) {
            setTransactionList();
            CallResetPagination();
            stopIsLoadingTransactionList();
            return;
          }
          setTransactionList(items.map(TransactionAdapter.toView));
          CallSetPagination(pagination);
          timer.startTimer();
          stopIsLoadingTransactionList();
        },
        (error: MySsException) => {
          setTransactionList();
          CallResetPagination();
          stopIsLoadingTransactionList();
          throw error;
        },
      );
    }

    async function dispatchGetRewardsAccounts(force = false) {
      if (!isActiveFeature.value) return;
      if (profile.value.isCommercial) return;
      if (!timer.isExpired.value && !force) return;

      startIsLoadingRewardsAccounts();
      await getRewardsAccounts(AUTH_STORE.session.currentEconomicGroupMember).then(
        ({ data }: MySsApiResponseData) => {
          allowRedeem.value = data.allowRedeem;
          timer.startTimer();
          stopIsLoadingRewardsAccounts();
        },
        (error: MySsException) => {
          allowRedeem.value = true;
          stopIsLoadingRewardsAccounts();
          throw error;
        },
      );
    }
    // #endregion

    // #region Events:
    async function onSessionOpen() {
      return Promise.all([
        dispatchGetBalance(),
        dispatchGetRewardsAccounts(),
        dispatchGetTransactions(),
      ]);
    }
    async function onChangeEconomicGroup() {
      transactionListFilters.value = new TransactionListFilters();
      return Promise.all([
        dispatchGetBalance(true),
        dispatchGetRewardsAccounts(true),
        dispatchGetTransactions(true),
      ]).then(() => {});
    }
    // #endregion

    return {
      balance: getBalanceState,
      transactionList: getTransactionListState,
      hasTransaction,
      transactionPagination: pagination,
      transactionListFilters,
      isActiveFeature,
      loadingTransactionList,
      loadingBalance,
      loadingRewardsAccounts,
      allowRedeem,
      setBalance,
      setTransactionList,
      startIsLoadingBalance,
      stopIsLoadingBalance,
      startIsLoadingRewardsAccounts,
      stopIsLoadingRewardsAccounts,
      startIsLoadingTransactionList,
      stopIsLoadingTransactionList,
      CallPaginateAndFetch,
      CallPaginate,
      dispatchMakeARansomRequest,
      dispatchGetBalance,
      dispatchGetRewardsAccounts,
      dispatchGetTransactions,
      onSessionOpen,
      onSessionClose: $reset,
      onChangeEconomicGroup,
      paginate,
      $reset,
    };
  },
  { persist: true },
);
